import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import jwtDecode from '@utils/jwtDecode';
import { HackleExperiment } from '@hackler/react-sdk';
import { useTheme, useMediaQuery } from '@material-ui/core';
import AppConfig from '@config';
import { CapaQuotationServiceType, CapaServiceType } from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import { RootState } from '@modules/index';
import ProfileUpdateDialog from '@components/Dialog/Account/ProfileUpdateDialog';
import CommonLayout from '@routes/Common/CommonLayout';
import RfqRequestPreview from '@routes/RfqRequestPreview';
import RequestProcessMethodContainer from '@routes/RequestProcessMethod';
import RequestForQuotationContainer from '@routes/RequestForQuotation';
import ServiceTypeSelectMobile from './ServiceType/ServiceTypeSelect-mobile';
import ServiceTypeSelect from './ServiceType/ServiceTypeSelect';
import ServiceTypeInformationDialog from './ServiceType/components/ServiceTypeInformationDialog';
import PCBRequest from './RequestTypes/PCB/PCBRequest';
import PCBStepRequestContainer from './RequestTypes/PCBStepRequest/PCBStepRequestContainer';
import DesignStepRequestContainer from './RequestTypes/DesignStepRequest/DesignStepRequestContainer';
import {
  PcbRequestStageEnum,
  DesignRequestStageEnum,
  RequestQueryString
} from './types';
import DesignRequest from './RequestTypes/Design/DesignRequest';
import RfqLeaveDialog from '@routes/RequestProcessMethod/Dialogs/RfqLeaveDialog';
import useRequest from '@hooks/useRequest';
import BottomNav from '@routes/Common/BottomNav';

const RequestPresenter = () => {
  const accessToken = useSelector<RootState>(
    (state) => state?.client.accessToken
  );
  const [profileUpdateDialog, setProfileUpdateDialog] = useState(false);
  const [rfqLeaveDialogOpen, setRfqLeaveDialogOpen] = useState(false);
  const [nextPath, setNextPath] = useState('');
  const { search, pathname } = useLocation();

  const history = useHistory();
  const isAuth = Boolean(accessToken);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    query: { stage, serviceType, detailedServiceType }
  } = useQueryString<RequestQueryString>();
  const [informationOn, setInformationOn] = useState<boolean>(false);
  const isDesign =
    serviceType && serviceType === CapaQuotationServiceType.Design;
  const isPcb = serviceType && serviceType === CapaQuotationServiceType.Pcb;
  const jwt = jwtDecode();
  const currentStage = useRef<PcbRequestStageEnum | DesignRequestStageEnum>(
    DesignRequestStageEnum.PROCESS_SELECT
  );

  // 파트너스 또는 커넥트에서 가입한 유저)
  const notAllowedUser = !jwt?.scopes.includes(CapaServiceType.Quotation);

  const { onInitRfqState } = useRequest();

  useEffect(() => {
    if (stage) {
      currentStage.current = stage;
    }
  }, [stage]);

  useEffect(() => {
    if (stage === DesignRequestStageEnum.COMPLETE) {
      history.replace('/dashboard', {
        isRfqsubmitted: true
      });
    }
    //eslint-disable-next-line
  }, [stage]);

  /**
   * Scrope에 Quotation이 없으면 필수정보 입력 모달이 뜸
   * (파트너스 또는 커넥트에서 가입한 유저)
   */
  useEffect(() => {
    if (!isAuth) return;

    if (!notAllowedUser) return;

    if (notAllowedUser) {
      const openControl = (e: MouseEvent & Partial<{ path: Array<any> }>) => {
        e.stopPropagation();

        const isGNB = e?.path?.some((v) => v?.localName === 'header');
        const isFooter = e?.path?.some((v) => v?.classList?.contains('footer'));

        const isProfileUpdateDialog = e?.path?.some(
          (v) => v?.id === 'profile-update'
        );

        if (isGNB || isFooter || isProfileUpdateDialog) {
          return;
        }

        return setProfileUpdateDialog(true);
      };

      window.addEventListener('mousedown', openControl);

      return () => {
        window.removeEventListener('mousedown', openControl);
      };
    }
  }, [notAllowedUser, isAuth]);

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const hanledPreviewDialogOpen = () => {
    setPreviewDialogOpen((prev) => !prev);
  };

  useEffect(() => {
    const unblock = history.block((location) => {
      const state = location.state as {
        openRfqLeaveDialog: boolean;
      };

      const openRfqLeaveDialog = state?.openRfqLeaveDialog;

      if (
        location.pathname !== pathname &&
        !rfqLeaveDialogOpen &&
        !previewDialogOpen &&
        openRfqLeaveDialog
      ) {
        setNextPath(location.pathname);
        setRfqLeaveDialogOpen((prev) => true);
        return false;
      }
    });

    return () => {
      unblock();
    };
  }, [history, rfqLeaveDialogOpen, previewDialogOpen, search, pathname]);

  if (!isAuth) {
    return <RfqRequestPreview />;
  }

  return (
    <>
      <RfqLeaveDialog
        open={rfqLeaveDialogOpen}
        onLeave={() => {
          history.push(nextPath);
          onInitRfqState();
        }}
        onClose={() => setRfqLeaveDialogOpen((prev) => false)}
      />

      <ServiceTypeInformationDialog
        open={informationOn}
        onClose={() => setInformationOn(false)}
      />

      {profileUpdateDialog && (
        <ProfileUpdateDialog
          open={profileUpdateDialog}
          onClose={() => setProfileUpdateDialog(false)}
        />
      )}

      {!serviceType &&
        (!matches ? (
          <ServiceTypeSelect onInformationOpen={() => setInformationOn(true)} />
        ) : (
          <ServiceTypeSelectMobile
            onInformationOpen={() => setInformationOn(true)}
          />
        ))}

      {serviceType && !isPcb && !isDesign && !detailedServiceType && (
        <CommonLayout bottomNav={<BottomNav />}>
          <RequestProcessMethodContainer />
        </CommonLayout>
      )}

      {serviceType && detailedServiceType && (
        <RequestForQuotationContainer
          hanledPreviewDialogOpen={hanledPreviewDialogOpen}
        />
      )}

      {isPcb && (
        <HackleExperiment
          experimentKey={AppConfig.mode === 'production' ? 6 : 5}
        >
          {(variation: 'A' | 'B') => {
            switch (variation) {
              case 'A':
                return <PCBRequest />;
              case 'B':
                return <PCBStepRequestContainer />;
              default:
                return <PCBRequest />;
            }
          }}
        </HackleExperiment>
      )}

      {isDesign && (
        <HackleExperiment
          experimentKey={AppConfig.mode === 'production' ? 8 : 7}
        >
          {(variation: 'A' | 'B') => {
            switch (variation) {
              case 'A':
                return <DesignRequest />;
              case 'B':
                return <DesignStepRequestContainer />;
              default:
                return <DesignRequest />;
            }
          }}
        </HackleExperiment>
      )}
    </>
  );
};

export default RequestPresenter;
